import mime from "mime";
import type {
  AppFiles,
  CacheEntry,
  RegisterAppResponse,
  ShouldUpdateAppResponse,
} from "./types";

/**
 * Get content type based on file extension using the mime package
 */
export function getContentType(filePath: string): string {
  const mimeType = mime.getType(filePath);
  return mimeType || "application/octet-stream";
}

/**
 * Strip protocol (https://) from baseUrl
 * Returns the domain-only part of the URL
 */
export function stripProtocol(baseUrl: string): string {
  return baseUrl.replace("https://", "");
}

/**
 * Extract app context from URL
 * Expects URLs like: /{baseUrl}/{project}/{appId}/appfiles/{filePath}
 * Returns null if pattern doesn't match
 *
 * Query parameters and hash fragments are excluded from the filePath.
 *
 * Examples:
 * - /api.cognitedata.com/test-project/test-app/appfiles/index.html -> { baseUrl: "api.cognitedata.com", project: "test-project", appId: "test-app", filePath: "index.html" }
 * - /api.cognitedata.com/test-project/test-app/appfiles/index.html?v=1.2.3 -> { baseUrl: "api.cognitedata.com", project: "test-project", appId: "test-app", filePath: "index.html" }
 * - /api.cognitedata.com/test-project/test-app/appfiles/assets/index.js -> { baseUrl: "api.cognitedata.com", project: "test-project", appId: "test-app", filePath: "assets/index.js" }
 * - /api.cognitedata.com/test-project/test-app/appfiles -> null (no file path)
 * - / -> null (root)
 */
export function extractAppContextFromUrl(url: URL): {
  baseUrl: string;
  project: string;
  appId: string;
  filePath: string;
} | null {
  // Pattern: /{baseUrl}/{project}/{appId}/appfiles/{filePath}
  // url.pathname already excludes query parameters and hash fragments
  const pathMatch = url.pathname.match(
    /^\/([^/]+)\/([^/]+)\/([^/]+)\/appfiles\/(.+)/,
  );

  if (!pathMatch) {
    return null;
  }

  return {
    baseUrl: decodeURIComponent(pathMatch[1]),
    project: decodeURIComponent(pathMatch[2]),
    appId: decodeURIComponent(pathMatch[3]),
    filePath: decodeURIComponent(pathMatch[4]),
  };
}

/**
 * Find a file in the cache by exact path match
 * Returns the ArrayBuffer if found, null otherwise
 */
export function findFileInCache(
  requestedPath: string,
  files: AppFiles,
): ArrayBuffer | null {
  return files[requestedPath] || null;
}

export function createCacheKey(
  baseUrl: string,
  project: string,
  appId: string,
): string {
  return `${baseUrl}::${project}::${appId}`;
}

export function shouldUpdateApp(
  appCaches: Map<string, CacheEntry>,
  baseUrl: string,
  project: string,
  appId: string,
  lastUpdatedTime: number,
): ShouldUpdateAppResponse {
  const cleanBaseUrl = stripProtocol(baseUrl);
  const cacheKey = createCacheKey(cleanBaseUrl, project, appId);
  const existingEntry = appCaches.get(cacheKey);

  if (!existingEntry) {
    return {
      shouldUpdate: true,
      message: `App ${appId} not cached, needs to be cached`,
      cacheKey,
    };
  }

  // Compare unix timestamps directly
  if (lastUpdatedTime > existingEntry.lastUpdatedTime) {
    return {
      shouldUpdate: true,
      message: `App ${appId} has newer version available`,
      cacheKey,
      currentLastUpdated: existingEntry.lastUpdatedTime,
    };
  }

  return {
    shouldUpdate: false,
    message: `App ${appId} cached version is ok`,
    cacheKey,
    currentLastUpdated: existingEntry.lastUpdatedTime,
  };
}

export function registerApp(
  appCaches: Map<string, CacheEntry>,
  baseUrl: string,
  project: string,
  appId: string,
  lastUpdatedTime: number,
  files: AppFiles,
): RegisterAppResponse {
  const cleanBaseUrl = stripProtocol(baseUrl);
  const cacheKey = createCacheKey(cleanBaseUrl, project, appId);

  // Cache the files
  appCaches.set(cacheKey, {
    files,
    lastUpdatedTime,
  });

  return {
    success: true,
    message: `App ${appId} cached successfully`,
    cacheKey,
  };
}
